import {Colors} from "../../colors";

// Syntax colors for the HTML code editor. Read lazily via a getter so dynamic
// (white-label) color overrides are respected.
const tokenColors = () => ({
  text: Colors.grey._800,
  comment: Colors.grey._400,
  doctype: Colors.grey._500,
  punctuation: Colors.grey._500,
  tagName: Colors.blue._700,
  attrName: Colors.orange._900,
  attrValue: Colors.green._900,
  mustache: Colors.pink._700,
});

// Liquid/handlebars-style template tokens, e.g. {{first_name}} — highlighted
// wherever they appear (text nodes and attribute values).
const MUSTACHE = /(\{\{[^}]*\}\})/g;

// Elements whose content is code, not email copy — it must not be treated as
// (bold) text content.
const RAW_TEXT_ELEMENTS = new Set(["script", "style"]);

const tagNameOf = (tag: string): string => {
  const match = tag.match(/^<\/?\s*([a-zA-Z][\w:-]*)/);
  return match ? match[1].toLowerCase() : "";
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const span = (color: string, text: string, bold = false): string =>
  `<span style="color:${color}${bold ? ";font-weight:600" : ""}">${escapeHtml(text)}</span>`;

// Wraps text that may contain mustache tokens, coloring the tokens and applying
// `baseColor` to the rest. `bold` emphasizes the non-token text (used for the
// email's actual text content, not tag/attribute syntax).
const highlightWithMustaches = (text: string, baseColor: string, bold = false): string =>
  text
    .split(MUSTACHE)
    .map((part) => {
      if (!part) return "";
      if (part.startsWith("{{")) return span(tokenColors().mustache, part, true);
      return span(baseColor, part, bold);
    })
    .join("");

// Finds the index of the `>` that closes the tag opened at `start`, ignoring any
// `>` that appears inside a quoted attribute value.
const findTagEnd = (code: string, start: number): number => {
  let quote = "";
  for (let i = start + 1; i < code.length; i += 1) {
    const ch = code[i];
    if (quote) {
      if (ch === quote) quote = "";
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === ">") {
      return i;
    }
  }
  return -1;
};

const highlightAttributes = (attrs: string): string => {
  const colors = tokenColors();
  // Matches, in order: whitespace, `=`, a quoted string, or a bare token
  // (attribute name or unquoted value).
  const attrRe = /(\s+)|(=)|("[^"]*"|'[^']*')|([^\s=/>"']+)/g;
  let result = "";
  let expectValue = false;
  let match: RegExpExecArray | null;

  while ((match = attrRe.exec(attrs)) !== null) {
    const [, whitespace, equals, quoted, bare] = match;
    if (whitespace) {
      result += escapeHtml(whitespace);
    } else if (equals) {
      result += span(colors.punctuation, "=");
      expectValue = true;
    } else if (quoted) {
      result += highlightWithMustaches(quoted, colors.attrValue);
      expectValue = false;
    } else if (bare) {
      result += expectValue
        ? highlightWithMustaches(bare, colors.attrValue)
        : span(colors.attrName, bare);
      expectValue = false;
    }
  }
  return result;
};

const highlightTag = (raw: string): string => {
  const colors = tokenColors();
  const open = raw.startsWith("</") ? "</" : "<";
  const close = raw.endsWith("/>") ? "/>" : ">";
  const inner = raw.slice(open.length, raw.length - close.length);

  const nameMatch = inner.match(/^([a-zA-Z][\w:-]*)/);
  let output = span(colors.punctuation, open);
  let attrs = inner;
  if (nameMatch) {
    output += span(colors.tagName, nameMatch[1]);
    attrs = inner.slice(nameMatch[1].length);
  }
  output += highlightAttributes(attrs);
  output += span(colors.punctuation, close);
  return output;
};

/**
 * Turns an HTML string into an HTML string of colored <span>s for display in a
 * read-only highlight layer. All text is HTML-escaped. Best-effort: malformed
 * input degrades to escaped text rather than throwing.
 */
export const highlightHtml = (code: string): string => {
  const colors = tokenColors();
  let output = "";
  let i = 0;

  while (i < code.length) {
    if (code[i] === "<") {
      if (code.startsWith("<!--", i)) {
        const end = code.indexOf("-->", i);
        const stop = end === -1 ? code.length : end + 3;
        output += span(colors.comment, code.slice(i, stop));
        i = stop;
        continue;
      }
      if (code[i + 1] === "!") {
        const end = code.indexOf(">", i);
        const stop = end === -1 ? code.length : end + 1;
        output += span(colors.doctype, code.slice(i, stop));
        i = stop;
        continue;
      }
      const end = findTagEnd(code, i);
      if (end === -1) {
        output += highlightWithMustaches(code.slice(i), colors.text, true);
        break;
      }
      const rawTag = code.slice(i, end + 1);
      output += highlightTag(rawTag);
      i = end + 1;

      // For <style>/<script>, emit the body as plain (non-bold) code rather than
      // as bold email text.
      const name = tagNameOf(rawTag);
      if (RAW_TEXT_ELEMENTS.has(name) && !rawTag.startsWith("</") && !rawTag.endsWith("/>")) {
        const rest = code.slice(i);
        const closeMatch = rest.match(new RegExp(`</\\s*${name}\\s*>`, "i"));
        if (closeMatch?.index !== undefined) {
          output += span(colors.text, rest.slice(0, closeMatch.index));
          output += highlightTag(closeMatch[0]);
          i += closeMatch.index + closeMatch[0].length;
        }
      }
      continue;
    }

    const next = code.indexOf("<", i);
    const stop = next === -1 ? code.length : next;
    output += highlightWithMustaches(code.slice(i, stop), colors.text, true);
    i = stop;
  }

  return output;
};
